/* eslint-disable max-len */
/* eslint-disable react/no-unused-prop-types */
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { withTranslation } from 'react-i18next';
import {
  Button,
  Dimmer,
  Grid,
  Header,
  Icon,
  Segment,
} from 'semantic-ui-react';
import { flashErrorMessage, flashSuccessMessage } from '../../actions/flash';
import { fetchM } from '../../actions/fetchM';
import { List } from '.';
import { namespaces, TRANSLATION_FILES } from '../../constants/translation';
import { StateAuth } from '../../types';
import { NoteEditor } from '../BookingNote';

class SharedNote extends Component {
  static propTypes = {
    auth: StateAuth,
    parentType: PropTypes.string,
    parentID: PropTypes.string,
    checkHasChanges: PropTypes.func,
    defaultPrivacy: PropTypes.string,
    hasEditAccess: PropTypes.bool,
    isDisabled: PropTypes.bool,
    onCheck: PropTypes.func,
    onExpand: PropTypes.func,
    setRef: PropTypes.func,
    showExpand: PropTypes.bool,
    token: PropTypes.string,
    t: PropTypes.func,
    appConfig: PropTypes.shape({
      data: PropTypes.shape({
        settings: PropTypes.shape({
          notesExternalPlaceholder: PropTypes.string,
        }),
      }),
    }),
  };
  static mapStateToProps = state => ({
    auth: state.auth,
    appConfig: state.appConfig,
  })
  static contextTypes = {
    store: PropTypes.object,
  }
  constructor(props) {
    super(props);
    this.state = {
      isRefreshing: false,
      loading: false,
      noteEditing: null,
      token: '',
      isExpanded: false,
    };
    this.listRef = React.createRef();
    this.editorRef = React.createRef();
    this.refreshCooldown = null;
  }
  componentDidMount() {
    if (this.props.setRef) {
      this.props.setRef(this);
    }
  }
  componentWillUnmount() {
    if (this.refreshCooldown !== null) clearInterval(this.refreshCooldown);
  }
  setComponentState(state) {
    return new Promise((resolve) => {
      this.setState((prevState) => {
        const nextState = typeof state === 'function' ? state(prevState) : state;
        return nextState;
      }, () => resolve(this.state));
    });
  }
  setLoading(loading = false) {
    return new Promise((res) => {
      this.setState({ loading }, () => {
        res(loading);
      });
    });
  }
  setRefreshCooldown = () => {
    this.refreshCooldown = setInterval(() => {
      this.setState({ isRefreshing: false });
      clearInterval(this.refreshCooldown);
    }, 5000); // 5 seconds
  }
  handleClickRefresh = async (e) => {
    if (e) e.preventDefault();
    if (this.listRef && this.listRef.current) {
      await this.setComponentState({ loading: true, isRefreshing: true });
      await this.listRef.current.fetchStateData(false);
      await this.setLoading(false);
      this.setRefreshCooldown();
    }
  }
  handleForceRefresh = async (prependData = false) => {
    if (this.listRef && this.listRef.current) {
      await this.setLoading(true);
      await this.listRef.current.fetchStateData(prependData);
      await this.setLoading(false);
    }
  }
  handleClickExpand = async (e) => {
    if (e) e.preventDefault();
    await this.setComponentState(prevState => ({ isExpanded: !prevState.isExpanded }));
    if (this.props.onExpand) this.props.onExpand(this.state.isExpanded);
  }
  // trigger from list, confirm delete button
  handleOnNoteDelete = async (bnID) => {
    // fix for loading dimmer
    let listPos = 0;
    if (this.listRef && this.listRef.current) {
      listPos = await this.listRef.current.getScrollPostion();
    }
    if (this.listRef && this.listRef.current) {
      await this.listRef.current.scrollToTop();
    }
    const { store } = this.context;
    const { t } = this.props;
    await this.setLoading(true);
    try {
      await store.dispatch(fetchM(`/shared_notes/${bnID}/`, {
        credentials: 'include',
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'X-Csrf-Token': this.state.token,
        },
      }));
    } catch (err) {
      await this.setLoading(false);
      if (err.res && err.res.status >= 400 && err.res.status < 500) {
        const message = await err.res.text();
        store.dispatch(flashErrorMessage({ message }));
        return;
      }
      Bugsnag.notifyException(err);
      await this.setLoading(false);
      await store.dispatch(flashErrorMessage({ message: `${t('There was an error with your request. Please try again', { ns: TRANSLATION_FILES.translation })}.` }));
      return;
    }
    if (this.listRef && this.listRef.current) {
      await this.listRef.current.deleteStateData(bnID);
    }
    await store.dispatch(flashSuccessMessage({
      header: t('Delete Success', { ns: TRANSLATION_FILES.translation }),
      message: t('Note successfully deleted', { ns: TRANSLATION_FILES.translation }),
    }));
    await this.setLoading(false);
    // fix for loading dimmer
    if (this.listRef && this.listRef.current) {
      await this.listRef.current.scrollToPosition(listPos);
    }
  }
  // trigger from list, edit button
  handleOnNoteEdit = async (bNote) => {
    await this.setComponentState({ noteEditing: bNote });
  }
  // trigger from note editor, cancel edit button
  handleEditorCancel = async () => {
    this.setState({ noteEditing: null });
  }
  // trigger from note editor, add button
  handleEditorAdd = async (encodedMDNote, privacyValue) => {
    if (this.listRef && this.listRef.current) {
      await this.listRef.current.scrollToTop();
    }
    const { store } = this.context;
    const { parentType, parentID, t } = this.props;
    await this.setLoading(true);
    const request = {
      parentType,
      parentID,
      note: encodedMDNote,
      privacy: privacyValue,
    };
    let res = '';
    try {
      res = await store.dispatch(fetchM('/shared_notes/', {
        credentials: 'include',
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Csrf-Token': this.state.token,
        },
        body: JSON.stringify(request),
      }));
    } catch (err) {
      await this.setLoading(false);
      if (err.res && err.res.status >= 400 && err.res.status < 500) {
        const message = await err.res.text();
        store.dispatch(flashErrorMessage({ message }));
        return;
      }
      Bugsnag.notifyException(err);
      await this.setLoading(false);
      await store.dispatch(flashErrorMessage({ message: `${t('There was an error with your request. Please try again', { ns: TRANSLATION_FILES.translation })}.` }));
      return;
    }
    if (!res.ok) {
      const message = await res.text();
      await store.dispatch(flashErrorMessage({ message }));
      await this.setLoading(false);
      return;
    }
    if (this.listRef && this.listRef.current) {
      await this.listRef.current.fetchStateData(true);
    }
    if (this.editorRef && this.editorRef.current) {
      await this.editorRef.current.resetEditor();
    }
    await store.dispatch(flashSuccessMessage({
      header: t('Add Note Success', { ns: TRANSLATION_FILES.translation }),
      message: t('Note successfully added', { ns: TRANSLATION_FILES.translation }),
    }));
    await this.setLoading(false);
  }
  // trigger from note editor, update button
  handleEditorUpdate = async (encodedMDNote, privacyValue) => {
    // fix for loading dimmer
    let listPos = 0;
    if (this.listRef && this.listRef.current) {
      listPos = await this.listRef.current.getScrollPostion();
    }
    if (this.listRef && this.listRef.current) {
      await this.listRef.current.scrollToTop();
    }
    const { store } = this.context;
    const { noteEditing } = this.state;
    const { t } = this.props;
    if (noteEditing && noteEditing.id !== '') {
      if (noteEditing.note !== encodedMDNote || noteEditing.privacy !== privacyValue) {
        await this.setLoading(true);
        const request = {
          ...noteEditing,
          note: encodedMDNote,
          privacy: privacyValue,
        };
        let res = '';
        try {
          res = await store.dispatch(fetchM(`/shared_notes/${request.id}/`, {
            credentials: 'include',
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
              'X-Csrf-Token': this.state.token,
            },
            body: JSON.stringify(request),
          }));
        } catch (err) {
          await this.setLoading(false);
          if (err.res && err.res.status >= 400 && err.res.status < 500) {
            const message = await err.res.text();
            store.dispatch(flashErrorMessage({ message }));
            return;
          }
          Bugsnag.notifyException(err);
          await this.setLoading(false);
          await store.dispatch(flashErrorMessage({ message: `${t('There was an error with your request. Please try again', { ns: TRANSLATION_FILES.translation })}.` }));
          return;
        }
        if (!res.ok) {
          const message = await res.text();
          await store.dispatch(flashErrorMessage({ message }));
          await this.setComponentState({ loading: false, noteEditing: null }); // reset all states
          return;
        }
        const bNote = await res.json();
        if (bNote && this.listRef && this.listRef.current) {
          await this.listRef.current.updateStateData(bNote);
        }
        if (this.editorRef && this.editorRef.current) {
          await this.editorRef.current.resetEditor(true);
        }
        await store.dispatch(flashSuccessMessage({
          header: t('Update Success', { ns: TRANSLATION_FILES.translation }),
          message: t('Note successfully updated', { ns: TRANSLATION_FILES.translation }),
        }));
        await this.setComponentState({ loading: false, noteEditing: null }); // reset all states
        // fix for loading dimmer
        if (bNote && this.listRef && this.listRef.current) {
          await this.listRef.current.scrollToPosition(listPos);
        }
        return;
      }
    }
    if (this.editorRef && this.editorRef.current) {
      await this.editorRef.current.resetEditor(true);
    }
    await this.setComponentState({ loading: false, noteEditing: null });
  }
  isInternalUser = () => {
    const user = this.props.auth;
    if (!user.companyBranch.isExternal) return true;
    return false;
  }
  render() {
    const { auth, isDisabled, hasEditAccess, parentType, parentID, showExpand, t } = this.props;
    const { loading, isExpanded, isRefreshing, noteEditing } = this.state;
    return (
      <Grid>
        <Grid.Row>
          <Grid.Column width={16} className={isExpanded ? '' : 'mobile-half-left-padding'}>
            <Segment.Group className={`booking-note ${showExpand ? 'expand-mode' : ''}`}>
              <Dimmer
                active={isDisabled}
                inverted
                style={{ zIndex: 20 }}
              />
              <Header size="tiny" textAlign="center" className="bn-header">{t('Notes')}</Header>
              <div>
                <Button
                  className="bn-refresh"
                  disabled={isRefreshing}
                  icon={<Icon name="refresh" loading={isRefreshing} fitted size="small" />}
                  onClick={this.handleClickRefresh}
                  title="Refresh"
                />
                {
                  showExpand ?
                    <Button
                      className="bn-expand"
                      icon={<Icon name={isExpanded ? 'compress' : 'expand'} fitted size="small" />}
                      onClick={this.handleClickExpand}
                      title={isExpanded ? 'Minimize' : 'Maximize'}
                    />
                    : ''
                }
              </div>
              <List
                auth={auth}
                parentType={parentType}
                parentID={parentID}
                hasEditAccess={hasEditAccess}
                noteEditingID={(noteEditing && noteEditing.id) || ''}
                noteOnDelete={this.handleOnNoteDelete}
                noteOnEdit={this.handleOnNoteEdit}
                ref={this.listRef}
                requestLoading={loading}
                isExpanded={isExpanded}
                onTokenReceived={(token) => {
                  this.setState({ token });
                }}
              />
              {
                hasEditAccess ?
                  <NoteEditor
                    bookingNote={noteEditing}
                    defaultPrivacy={this.props.defaultPrivacy}
                    loading={loading}
                    onAdd={this.handleEditorAdd}
                    onCancel={this.handleEditorCancel}
                    onUpdate={this.handleEditorUpdate}
                    ref={this.editorRef}
                    showInternalPrivacy={this.isInternalUser()}
                    auth={this.props.auth}
                    settings={this.props.appConfig && this.props.appConfig.data && this.props.appConfig.data.settings}
                  />
                : ''
              }
            </Segment.Group>
          </Grid.Column>
        </Grid.Row>
      </Grid>
    );
  }
}

export default withTranslation(namespaces)(connect(SharedNote.mapStateToProps)(SharedNote));



// WEBPACK FOOTER //
// ./src/components/SharedNote/SharedNote.jsx